import React from "react";
import {
  MapContainer,
  TileLayer,
  Polyline,
  CircleMarker,
  Tooltip,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { LatLngExpression } from "leaflet";

export interface GPSPoint {
  time: string;
  latitude: number;
  longitude: number;
  angle?: number;
  speed?: number;
}

interface MapProps {
  data: GPSPoint[];
}

const getColorBySpeed = (speed: number = 0) => {
  if (speed < 10) return "blue";
  if (speed < 30) return "green";
  if (speed < 50) return "yellow";
  if (speed < 70) return "orange";
  if (speed < 100) return "red";
  return "purple";
};

const GPSMap: React.FC<MapProps> = ({ data }) => {
  if (!data.length) return <div>No GPS data available</div>;

  const segments = data.slice(1).map((point, i) => {
    const prev = data[i];
    return {
      positions: [
        [prev.latitude, prev.longitude],
        [point.latitude, point.longitude],
      ] as [number, number][],
      color: getColorBySpeed(point.speed),
    };
  });

  const center: LatLngExpression = [data[0].latitude, data[0].longitude];

  return (
    <div style={{ height: "500px", width: "100%", position: "relative" }}>
      <MapContainer
        center={center}
        zoom={13}
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />

        {segments.map((seg, idx) => (
          <Polyline
            key={idx}
            positions={seg.positions}
            pathOptions={{ color: seg.color, weight: 4 }}
          />
        ))}

        {data.map((point, i) => (
          <CircleMarker
            key={i}
            center={[point.latitude, point.longitude]}
            radius={5}
            pathOptions={{ color: getColorBySpeed(point.speed) }}
          >
            <Tooltip>
              <div>
                <b>Time:</b> {point.time} <br />
                <b>Speed:</b> {point.speed} km/h <br />
                {point.angle !== undefined && <><b>Angle:</b> {point.angle}° <br /></>}
                <b>Lat:</b> {point.latitude.toFixed(5)} <br />
                <b>Lon:</b> {point.longitude.toFixed(5)}
              </div>
            </Tooltip>
          </CircleMarker>
        ))}
      </MapContainer>

      <div
        style={{
          position: "absolute",
          bottom: 10,
          left: 10,
          background: "white",
          padding: "10px",
          borderRadius: "5px",
          boxShadow: "0 0 5px rgba(0,0,0,0.3)",
          zIndex: 1000,
        }}
      >
        <b>Speed Legend (km/h)</b>
        <div style={{ display: "flex", marginTop: "5px", gap: "5px" }}>
          {[
            { color: "blue", label: "<10" },
            { color: "green", label: "10-29" },
            { color: "yellow", label: "30-49" },
            { color: "orange", label: "50-69" },
            { color: "red", label: "70-99" },
            { color: "purple", label: "100+" },
          ].map((item) => (
            <div
              key={item.label}
              style={{ display: "flex", alignItems: "center", gap: "3px" }}
            >
              <div
                style={{
                  width: "15px",
                  height: "15px",
                  background: item.color,
                }}
              ></div>
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GPSMap;
